import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getInvoicesOptions,
	generateMonthlyInvoiceMutation,
	generateMonthlyInvoicesForAllParentsMutation,
	resendInvoiceMutation,
	processManualPaymentMutation,
	refundPaymentMutation,
	getAllInvoicesOptions,
	getAllInvoicesQueryKey,
	generateMonthlyInvoicesForAllParentsDraftMutation,
	getAllParentsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import type { Invoice } from "@/lib/client/types.gen";
import { client } from "@/lib/client/client.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from "sonner";
import {
	Receipt,
	Plus,
	Send,
	Euro,
	CheckCircle,
	Clock,
	Eye,
	Undo,
	FileText,
	ChevronsUpDown,
	Check,
	User,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { cn, normalizeString } from "@/lib/utils";
import { getSchoolLabel } from "@/stores/enum-store";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterFaceted,
	DataTableFilterText,
} from "@/components/data-table";

export const Route = createFileRoute("/admin/invoices/")({
	component: InvoicesManagementPage,
});

const statusFilterOptions = [
	{ label: "Brouillon", value: "DRAFT" },
	{ label: "En attente", value: "PENDING" },
	{ label: "Payées", value: "PAID" },
	{ label: "Partiellement payées", value: "PARTIALLY_PAID" },
	{ label: "Échec", value: "FAILED" },
	{ label: "Annulées", value: "CANCELLED" },
];

function InvoicesManagementPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [selectedDueDate, setSelectedDueDate] = useState<string>("");
	const [startingCounter, setStartingCounter] = useState<number>(1);
	const [singleParentOpen, setSingleParentOpen] = useState(false);
	const [parentSearchOpen, setParentSearchOpen] = useState(false);
	const [selectedParentId, setSelectedParentId] = useState<string>("");
	const { data: invoicesData, isLoading } = useQuery(getAllInvoicesOptions());
	const invoices = (invoicesData as any as Invoice[]) || [];

	const { data: parentsData } = useQuery({
		...getAllParentsOptions(),
		enabled: generateDialogOpen,
	});
	const parents = (parentsData as any as any[]) || [];
	const selectedParent = parents.find((p) => p.id === selectedParentId);

	const draftInvoices = invoices.filter((inv) => inv.status === "DRAFT");
	const hasDraftInvoices = draftInvoices.length > 0;

	const generateInvoices = useMutation({
		...generateMonthlyInvoicesForAllParentsDraftMutation(),
		onSuccess: (data: any) => {
			const count = Array.isArray(data) ? data.length : 0;
			toast.success(`${count} facture(s) brouillon générée(s)`, {
				description: "Consultez la page de preview pour les vérifier avant de les envoyer.",
			});
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
			setGenerateDialogOpen(false);
			navigate({ href: "/admin/invoices/preview" });
		},
		onError: () => {
			toast.error("Erreur lors de la génération des factures");
		},
	});

	const generateSingleParentInvoice = useMutation({
		mutationFn: async () => {
			const period = `${selectedYear}-${selectedMonth.toString().padStart(2, "0")}`;
			const params = new URLSearchParams({
				period,
				startingCounter: String(startingCounter),
			});
			if (selectedDueDate) {
				params.set("dueDate", selectedDueDate);
			}
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/invoices/generate-draft/${selectedParentId}?${params}`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				},
			);
			if (!response.ok) {
				const message = await response.text().catch(() => "");
				throw new Error(message || "Erreur lors de la génération");
			}
			const text = await response.text();
			return text ? JSON.parse(text) : null;
		},
		onSuccess: (data: any) => {
			if (!data) {
				toast.warning(
					"Aucune facture générée : ce parent n'a pas d'inscription active sur la période.",
				);
				return;
			}
			toast.success("Facture brouillon générée", {
				description:
					"Consultez la page de preview pour la vérifier avant de l'envoyer.",
			});
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
			setGenerateDialogOpen(false);
			navigate({ href: "/admin/invoices/preview" });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Erreur lors de la génération de la facture");
		},
	});

	const resendInvoice = useMutation({
		...resendInvoiceMutation(),
		onSuccess: () => {
			toast.success("Email de facture renvoyé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi de l'email de facture");
		},
	});

	const processManualPayment = useMutation({
		...processManualPaymentMutation(),
		onSuccess: () => {
			toast.success("Paiement enregistré avec succès");
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de l'enregistrement du paiement");
		},
	});

	const refundPayment = useMutation({
		...refundPaymentMutation(),
		onSuccess: () => {
			toast.success("Remboursement effectué avec succès");
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors du remboursement");
		},
	});

	const handleGenerateInvoices = () => {
		const period = `${selectedYear}-${selectedMonth.toString().padStart(2, "0")}`;
		const today = new Date().toISOString().split("T")[0];

		if (selectedDueDate && selectedDueDate < today) {
			toast.error("La date d'échéance ne peut pas être antérieure à aujourd'hui");
			return;
		}

		generateInvoices.mutate({
			query: {
				period,
				dueDate: selectedDueDate || undefined,
				startingCounter,
			}
		});
	};

	const handleGenerateSingleParentInvoice = () => {
		if (!selectedParentId) {
			toast.error("Veuillez sélectionner un parent");
			return;
		}
		const today = new Date().toISOString().split("T")[0];
		if (selectedDueDate && selectedDueDate < today) {
			toast.error("La date d'échéance ne peut pas être antérieure à aujourd'hui");
			return;
		}
		generateSingleParentInvoice.mutate();
	};

	const handleResendInvoice = (invoiceId: string) => {
		resendInvoice.mutate({
			path: { id: invoiceId } as any,
		});
	};

	// const handleManualPayment = (invoiceId: string, amount: number) => {
	//   processManualPayment.mutate({
	//     body: {
	//       invoiceId,
	//       amount,
	//       paymentMethod: 'BANK_TRANSFER' as any,
	//       paymentProvider: 'OTHER' as any,
	//     } as any,
	//   });
	// };

	const columns = useMemo<ColumnDef<Invoice>[]>(
		() => [
			{
				accessorKey: "invoiceNumber",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Numéro" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<FileText className="h-4 w-4 text-gray-400" />
						<span className="font-medium">
							#{row.getValue("invoiceNumber")}
						</span>
					</div>
				),
				meta: { label: "Numéro" },
			},
			{
				id: "parent",
				accessorFn: (row) =>
					(row as any).parent?.lastName + " " + (row as any).parent?.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => {
					const invoice = row.original as any;
					return (
						<div>
							<p className="font-medium">
								{invoice.parent?.firstName} {invoice.parent?.lastName}
							</p>
						</div>
					);
				},
				filterFn: (row, columnId, filterValue: string) => {
					const invoice = row.original as any;
					const searchValue = normalizeString(filterValue);

					// Search in parent name
					const fullName = normalizeString(
						`${invoice.parent?.firstName || ""} ${invoice.parent?.lastName || ""}`
					);
					const reverseName = normalizeString(
						`${invoice.parent?.lastName || ""} ${invoice.parent?.firstName || ""}`
					);
					if (fullName.includes(searchValue) || reverseName.includes(searchValue)) {
						return true;
					}

					// Search in invoice number
					const invoiceNumber = normalizeString(invoice.invoiceNumber || "");
					if (invoiceNumber.includes(searchValue)) {
						return true;
					}

					// Search in children names
					const students = invoice.parent?.students || [];
					for (const student of students) {
						const studentFullName = normalizeString(
							`${student.firstName || ""} ${student.lastName || ""}`
						);
						const studentReverseName = normalizeString(
							`${student.lastName || ""} ${student.firstName || ""}`
						);
						if (studentFullName.includes(searchValue) || studentReverseName.includes(searchValue)) {
							return true;
						}
					}

					return false;
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
                id: "enfants",
                accessorFn: (row) => row.parent.id + "_enfants",
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Enfants" />
                ),
                cell: ({ row }) => {
                    const invoice = row.original as any;
                    return (
                        <div className="text-sm">
                            {invoice.parent?.students && invoice.parent.students.length > 0 ? (
                                invoice.parent.students.map((student: any, idx: number) => (
                                    <div key={idx} className="text-gray-700">
                                        {student.firstName} {student.lastName}
                                    </div>
                                ))
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                        </div>
                    );
                },
                meta: {
                    label: "Enfants",
                    textValue: (row: Invoice) => {
                        const students = (row as any).parent?.students || [];
                        return students.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ") || "-";
                    },
                },
            },
			{
				id: "classe",
				accessorFn: (row) => {
					const students = (row as any).parent?.students || [];
					return students.map((s: any) => s.className || "-").join(", ");
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Classe" />
				),
				cell: ({ row }) => {
					const invoice = row.original as any;
					return (
						<div className="text-sm">
							{invoice.parent?.students && invoice.parent.students.length > 0 ? (
								invoice.parent.students.map((student: any, idx: number) => (
									<div key={idx} className="text-gray-700">
										{student.className || "-"}
									</div>
								))
							) : (
								<span className="text-gray-400">-</span>
							)}
						</div>
					);
				},
				meta: {
					label: "Classe",
					textValue: (row: Invoice) => {
						const students = (row as any).parent?.students || [];
						return students.map((s: any) => s.className || "-").join(", ");
					},
				},
			},
			{
				id: "etablissement",
				accessorFn: (row) => {
					const students = (row as any).parent?.students || [];
					const schools = [...new Set(students.map((s: any) => getSchoolLabel(s.school)))];
					return schools.join(", ") || "-";
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Établissement" />
				),
				cell: ({ row }) => (
					<span className="text-sm text-gray-600">
						{row.getValue("etablissement")}
					</span>
				),
				meta: { label: "Établissement" },
			},
			{
				id: "period",
				accessorFn: (row) => {
					if (row.billingPeriodStart && row.billingPeriodEnd) {
						return `${format(new Date(row.billingPeriodStart), "dd/MM", { locale: fr })} - ${format(new Date(row.billingPeriodEnd), "dd/MM/yy", { locale: fr })}`;
					}
					return "Non définie";
				},
				header: "Période",
				cell: ({ row }) => (
					<span className="text-sm">
						{row.getValue("period")}
					</span>
				),
				enableSorting: false,
				meta: { label: "Période" },
			},
			{
				accessorKey: "totalAmount",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Montant" />
				),
				cell: ({ row }) => {
					const invoice = row.original as any;
					return (
						<div>
							<p className="font-medium">
								{invoice.totalAmount?.toFixed(2)} €
							</p>
							{invoice.paidAmount && invoice.paidAmount > 0 && (
								<p className="text-xs text-green-600">
									Payé: {invoice.paidAmount.toFixed(2)} €
								</p>
							)}
							{invoice.remainingAmount &&
								invoice.remainingAmount > 0 && (
									<p className="text-xs text-red-600">
										Reste: {invoice.remainingAmount.toFixed(2)} €
									</p>
								)}
						</div>
					);
				},
				meta: {
					label: "Montant",
					textValue: (row: Invoice) => `${row.totalAmount?.toFixed(2)} €`,
				},
			},
			{
				accessorKey: "status",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Statut" />
				),
				cell: ({ row }) => {
					const invoice = row.original as any;
					return (
						<Badge
							variant={
								invoice.status === "DRAFT"
									? "secondary"
									: invoice.status === "PAID"
										? "default"
										: invoice.status === "PENDING"
											? "secondary"
											: invoice.status === "PARTIALLY_PAID"
												? "secondary"
												: invoice.status === "FAILED"
													? "destructive"
													: "outline"
							}
							className={invoice.status === "DRAFT" ? "bg-yellow-100 text-yellow-800" : ""}
						>
							{invoice.status === "DRAFT" && "Brouillon"}
							{invoice.status === "PAID" && "Payée"}
							{invoice.status === "PENDING" && "En attente"}
							{invoice.status === "PARTIALLY_PAID" && "Partielle"}
							{invoice.status === "FAILED" && "Échec"}
							{invoice.status === "CANCELLED" && "Annulée"}
						</Badge>
					);
				},
				filterFn: (row, id, value: string[]) =>
					value.includes(String(row.getValue(id))),
				meta: {
					label: "Statut",
					textValue: (row: Invoice) => {
						const statusLabels: Record<string, string> = {
							DRAFT: "Brouillon",
							PAID: "Payée",
							PENDING: "En attente",
							PARTIALLY_PAID: "Partielle",
							FAILED: "Échec",
							CANCELLED: "Annulée",
						};
						return statusLabels[row.status || ""] || row.status || "";
					},
				},
			},
			{
				accessorKey: "dueDate",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Échéance" />
				),
				cell: ({ row }) => {
					const invoice = row.original as any;
					return invoice.dueDate ? (
						<span
							className={cn(
								"text-sm",
								new Date(invoice.dueDate) < new Date() &&
									invoice.status !== "PAID" &&
									"text-red-600 font-medium",
							)}
						>
							{format(new Date(invoice.dueDate), "dd/MM/yyyy", {
								locale: fr,
							})}
						</span>
					) : (
						"Non définie"
					);
				},
				meta: {
					label: "Échéance",
					textValue: (row: Invoice) =>
						row.dueDate ? format(new Date(row.dueDate), "dd/MM/yyyy", { locale: fr }) : "Non définie",
				},
			},
			{
				id: "actions",
				header: "Actions",
				cell: ({ row }) => {
					const invoice = row.original as any;
					return (
						<div className="flex items-center gap-2">
							<Button
								size="sm"
								variant="outline"
								onClick={() =>
									navigate({ to: `/admin/invoices/${invoice.id}` })
								}
							>
								<Eye className="h-3 w-3" />
							</Button>
							{invoice.status !== "PAID" && (
								<Button
									size="sm"
									variant="outline"
									onClick={() => handleResendInvoice(invoice.id || "")}
									disabled={resendInvoice.isPending}
								>
									<Send className="h-3 w-3" />
								</Button>
							)}
							{invoice.status === "PAID" && (
								<Button
									size="sm"
									variant="outline"
									onClick={() => {
										if (
											confirm(
												"Êtes-vous sûr de vouloir rembourser cette facture ?",
											)
										) {
											refundPayment.mutate({
												path: {
													paymentId: invoice.payments?.[0]?.id || "",
												},
												body: { amount: invoice.paidAmount || 0 },
											});
										}
									}}
									disabled={refundPayment.isPending}
								>
									<Undo className="h-3 w-3" />
								</Button>
							)}
						</div>
					);
				},
				enableSorting: false,
				meta: { label: "Actions" },
			},
		],
		[navigate, resendInvoice.isPending, refundPayment],
	);

	const stats = {
		total: invoices.length,
		pending: invoices.filter((inv: any) => inv.status === "PENDING").length,
		paid: invoices.filter((inv: any) => inv.status === "PAID").length,
		partiallyPaid: invoices.filter(
			(inv: any) => inv.status === "PARTIALLY_PAID",
		).length,
		failed: invoices.filter((inv: any) => inv.status === "FAILED").length,
		totalAmount: invoices.reduce(
			(acc: any, inv: any) => acc + (inv.totalAmount || 0),
			0,
		),
		paidAmount: invoices.reduce(
			(acc: any, inv: any) => acc + (inv.paidAmount || 0),
			0,
		),
		pendingAmount: invoices
			.filter((inv: any) => inv.status === "PENDING")
			.reduce((acc: any, inv: any) => acc + (inv.totalAmount || 0), 0),
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des factures...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{hasDraftInvoices && (
				<Card className="border-yellow-500 bg-yellow-50">
					<CardContent className="pt-6">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<Receipt className="h-5 w-5 text-yellow-600" />
								<div>
									<p className="font-medium text-yellow-900">
										{draftInvoices.length} facture(s) en mode brouillon
									</p>
									<p className="text-sm text-yellow-700">
										Vérifiez et confirmez les factures avant de les envoyer aux parents
									</p>
								</div>
							</div>
							<Button
								onClick={() => navigate({ href: "/admin/invoices/preview" })}
								className="bg-yellow-600 hover:bg-yellow-700"
							>
								<Eye className="mr-2 h-4 w-4" />
								Voir le preview
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des factures</h1>
					<p className="text-gray-600 mt-2">
						Gérez les factures et les paiements
					</p>
				</div>
				<Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Générer les factures mensuelles
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Générer les factures mensuelles</DialogTitle>
							<DialogDescription>
								Sélectionnez le mois et l'année pour générer les factures en mode preview.
								Vous pourrez les vérifier avant de les envoyer aux parents.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div>
								<Label>Mois</Label>
								<Select
									value={selectedMonth.toString()}
									onValueChange={(v) => setSelectedMonth(parseInt(v))}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1">Janvier</SelectItem>
										<SelectItem value="2">Février</SelectItem>
										<SelectItem value="3">Mars</SelectItem>
										<SelectItem value="4">Avril</SelectItem>
										<SelectItem value="5">Mai</SelectItem>
										<SelectItem value="6">Juin</SelectItem>
										<SelectItem value="7">Juillet</SelectItem>
										<SelectItem value="8">Août</SelectItem>
										<SelectItem value="9">Septembre</SelectItem>
										<SelectItem value="10">Octobre</SelectItem>
										<SelectItem value="11">Novembre</SelectItem>
										<SelectItem value="12">Décembre</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Année</Label>
								<Input
									type="number"
									value={selectedYear}
									onChange={(e) => setSelectedYear(parseInt(e.target.value))}
									min={2024}
									max={2030}
								/>
							</div>
							<div>
								<Label>Date d'échéance (optionnel)</Label>
								<Input
									type="date"
									value={selectedDueDate}
									onChange={(e) => setSelectedDueDate(e.target.value)}
									min={new Date().toISOString().split("T")[0]}
								/>
								<p className="text-xs text-gray-500 mt-1">
									Par défaut : fin du mois de facturation
								</p>
							</div>
							<div>
								<Label>Numéro de compteur de départ</Label>
								<Input
									type="number"
									value={startingCounter}
									onChange={(e) => setStartingCounter(parseInt(e.target.value) || 1)}
									min={1}
									placeholder="Ex: 185102"
								/>
								<p className="text-xs text-gray-500 mt-1">
									Les factures seront numérotées INV-XXXXXX en partant de ce numéro (ex: INV-185102, INV-185103, ...)
								</p>
							</div>

							<Collapsible
								open={singleParentOpen}
								onOpenChange={setSingleParentOpen}
								className="border rounded-lg"
							>
								<CollapsibleTrigger asChild>
									<button
										type="button"
										className="flex w-full items-center justify-between p-3 text-sm font-medium hover:bg-gray-50"
									>
										<span className="flex items-center gap-2">
											<User className="h-4 w-4" />
											Générer pour un parent spécifique
										</span>
										<ChevronsUpDown className="h-4 w-4 text-gray-400" />
									</button>
								</CollapsibleTrigger>
								<CollapsibleContent className="space-y-3 p-3 pt-0">
									<p className="text-xs text-gray-500">
										Génère une facture brouillon uniquement pour le parent
										sélectionné, en utilisant le mois, l'échéance et le numéro de
										compteur ci-dessus.
									</p>
									<div>
										<Label>Parent</Label>
										<Popover
											open={parentSearchOpen}
											onOpenChange={setParentSearchOpen}
										>
											<PopoverTrigger asChild>
												<Button
													variant="outline"
													role="combobox"
													className="w-full justify-between font-normal"
												>
													{selectedParent
														? `${selectedParent.firstName} ${selectedParent.lastName}`
														: "Rechercher un parent..."}
													<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
												</Button>
											</PopoverTrigger>
											<PopoverContent
												className="w-[var(--radix-popover-trigger-width)] p-0"
												align="start"
											>
												<Command
													filter={(value, search) =>
														value.toLowerCase().includes(search.toLowerCase())
															? 1
															: 0
													}
												>
													<CommandInput placeholder="Nom, prénom, email..." />
													<CommandList>
														<CommandEmpty>Aucun parent trouvé.</CommandEmpty>
														<CommandGroup>
															{parents.map((parent) => {
																const searchValue = `${parent.firstName} ${parent.lastName} ${parent.email || ""}`;
																return (
																	<CommandItem
																		key={parent.id}
																		value={searchValue}
																		onSelect={() => {
																			setSelectedParentId(parent.id);
																			setParentSearchOpen(false);
																		}}
																	>
																		<Check
																			className={cn(
																				"mr-2 h-4 w-4",
																				selectedParentId === parent.id
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																		<div className="flex flex-col">
																			<span>
																				{parent.firstName} {parent.lastName}
																			</span>
																			{parent.email && (
																				<span className="text-xs text-gray-500">
																					{parent.email}
																				</span>
																			)}
																		</div>
																	</CommandItem>
																);
															})}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
									</div>
									<Button
										className="w-full"
										variant="secondary"
										onClick={handleGenerateSingleParentInvoice}
										disabled={
											generateSingleParentInvoice.isPending || !selectedParentId
										}
									>
										{generateSingleParentInvoice.isPending
											? "Génération..."
											: "Générer pour ce parent"}
									</Button>
								</CollapsibleContent>
							</Collapsible>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setGenerateDialogOpen(false)}
							>
								Annuler
							</Button>
							<Button
								onClick={handleGenerateInvoices}
								disabled={generateInvoices.isPending}
							>
								{generateInvoices.isPending
									? "Génération..."
									: "Générer le preview"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Statistiques */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total factures
						</CardTitle>
						<Receipt className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats.total}</div>
						<p className="text-xs text-muted-foreground">
							{stats.pending} en attente
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Montant total</CardTitle>
						<Euro className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{stats.totalAmount.toFixed(0)} €
						</div>
						<p className="text-xs text-muted-foreground">Facturé ce mois</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Encaissé</CardTitle>
						<CheckCircle className="h-4 w-4 text-green-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{stats.paidAmount.toFixed(0)} €
						</div>
						<p className="text-xs text-muted-foreground">
							{stats.paid} factures payées
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">En attente</CardTitle>
						<Clock className="h-4 w-4 text-yellow-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-yellow-600">
							{stats.pendingAmount.toFixed(0)} €
						</div>
						<p className="text-xs text-muted-foreground">À encaisser</p>
					</CardContent>
				</Card>
			</div>

			{/* Filtres et liste des factures */}
			<Card>
				<CardHeader>
					<CardTitle>Liste des factures</CardTitle>
					<CardDescription>Toutes les factures générées</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={invoices}
						initialColumnVisibility={{ etablissement: false }}
						emptyMessage="Aucune facture trouvée"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="factures">
								<DataTableFilterText
									column={table.getColumn("parent")!}
									placeholder="Parent, enfant, n° facture..."
								/>
								<DataTableFilterFaceted
									column={table.getColumn("status")!}
									title="Statut"
									options={statusFilterOptions}
								/>
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
